import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Menu, Button, MenuItem, Typography } from '@material-ui/core';

import { useStyles, customCardStyles, collapseCardStyles } from './Details.styled';
import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { buildUrl, forceTimezone } from 'utils';
import Status from 'components/Status';
import Loading from 'components/Loading';
import Priority from 'components/Priority';
import { AlarmsActions } from 'state/modules/alarms';
import { useHistory, withRouter } from 'react-router-dom';
import { priorities, statuses } from 'constants/alarms';
import { CreateEditTaskModal } from 'components/Modals';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Card, CollapseCard, ItemDetails } from 'components/UI';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const AlarmDetails = ({ id, related }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const cardClasses = customCardStyles();
  const [anchorEl, setAnchorEl] = useState({});
  const collapseCardClasses = collapseCardStyles();
  const [openCreateTaskModal, setOpenTaskModal] = useState(false);
  const { current, alarmLoading } = useSelector((state) => state.alarms);
  const { fetchedCreateTask } = useSelector((state) => state.tasks || {});
  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Handle open/close create task modal
   */
  const handleOpenCreateTaskModal = () => setOpenTaskModal((prevValue) => !prevValue);

  /**
   * Open menu
   * @param {*} event
   * @param {*} el
   */
  const handleClick = (event, el) => {
    setAnchorEl({
      ...anchorEl,
      [el]: event.currentTarget,
    });
  };

  /**
   * Close Menu
   */
  const handleClose = (el, payload) => {
    if (
      payload &&
      ((payload.active !== null && current.active !== payload.active) ||
        (payload.priority && current.current_priority !== payload.priority))
    ) {
      updateAlarm(payload);
    }
    setAnchorEl({
      ...anchorEl,
      [el]: null,
    });
  };

  const handleItemClick = () => {
    setAnchorEl(null);
  };

  const updateAlarm = (payload) => {
    dispatch(AlarmsActions.fetchUpdateAlarm(id, payload));
  };

  useEffect(() => {
    if (id) {
      dispatch(AlarmsActions.fetchAlarmById(id));
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (id) {
      dispatch(AlarmsActions.fetchAlarmById(id));
    }
  }, [fetchedCreateTask]) // eslint-disable-line

  const updateStatus = (status) => {
    if (current.status !== status) {
      updateAlarm({ action: 'set_status', action_options: { status } });
    }
    handleClose('status');
  };

  const renderCardHeader = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="h3">
        <Box fontSize={15} fontWeight="500">
          {i18n.t('alarm.alarm_id') + `A - ${id}`}
        </Box>
      </Typography>
      {current?.status && (
        <Can I="update" a="conditional_instance">
          <Button
            aria-haspopup="true"
            aria-controls="status-menu"
            style={{ textTransform: 'capitalize' }}
            onClick={(e) => handleClick(e, 'status')}
          >
            <Status status={current.status} statusKey="alarms_statuses" />
            <KeyboardArrowDownIcon className={classes.statusIcon} />
          </Button>
          <Menu
            keepMounted
            id="statuses-menu"
            anchorEl={anchorEl?.status}
            open={Boolean(anchorEl?.status)}
            onClose={() => handleClose('status')}
          >
            {statuses?.map((status, index) => {
              const newStatus = status.value ? 'OPEN' : 'CLOSED';
              return (
                <MenuItem key={index} onClick={() => updateStatus(newStatus)}>
                  {status.label}
                </MenuItem>
              );
            })}
          </Menu>
        </Can>
      )}
    </Box>
  );

  /**
   * Title of task card
   */
  const taskTitle = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography variant="h4">
        <Box fontSize={13} fontWeight="fontWeightMedium" className={classes.textSecondary}>
          {i18n.t('tasks.title')}
        </Box>
      </Typography>
      <Button
        variant="text"
        color="primary"
        component="span"
        className={classes.addButton}
        startIcon={<AddCircleIcon />}
        onClick={handleOpenCreateTaskModal}
      >
        {i18n.t('alarm.add_task')}
      </Button>
    </Box>
  );

  const handleTagClick = (type, tagId) => {
    switch (type) {
      case 'Account': {
        history.push(buildUrl(`/employees/${tagId}`));
        return;
      }
      case 'User': {
        history.push(buildUrl(`/customers/${tagId}`));
        return;
      }
      case 'Vehicle': {
        history.push(buildUrl(`/vehicles/${tagId}`));
        return;
      }
      case 'Reservation': {
        history.push(buildUrl(`/reservations/${tagId}`));
        return;
      }
      case 'Invoice': {
        history.push(buildUrl(`/invoice/${tagId}`));
        return;
      }
      case 'Subscription': {
        history.push(buildUrl(`/subscriptions/${tagId}`));
        return;
      }
      default:
        return;
    }
  };

  return (
    <Card title={renderCardHeader} headerActionType="vertical" contentRootClasses={cardClasses}>
      <>
        {alarmLoading ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            <Box className={classes.cardCustomPadding}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h3">
                  <Box fontSize={16} fontWeight="fontWeightMedium">
                    {current?.name || '--'}
                  </Box>
                </Typography>
                {current.current_priority && (
                  <>
                    <Button
                      aria-haspopup="true"
                      aria-controls="priority-menu"
                      style={{ display: 'none' }}
                      className={classes.menuButton}
                      onClick={(e) => handleClick(e, 'priority')}
                    >
                      <Priority priority={current.current_priority} gutterBottom={false} />
                      <KeyboardArrowDownIcon className={classes.prioritiesIcon} />
                    </Button>
                    <Menu
                      id="priority-menu-items"
                      keepMounted={false}
                      anchorEl={anchorEl?.priority}
                      open={Boolean(anchorEl?.priority)}
                      onClose={() => handleClose('priority')}
                    >
                      {priorities?.map((priority, index) => {
                        const payload = {
                          priority: priority.value,
                        };
                        return (
                          <MenuItem key={index} onClick={() => handleClose('priority', payload)}>
                            {priority.label}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
            <Box className={classes.cardContentPadding}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h4" className={classes.textSecondary}>
                    <Box fontSize={13} fontWeight="fontWeightMedium">
                      {i18n.t('alarm.create_date')}:
                    </Box>
                  </Typography>
                  {current?.created_time && (
                    <Typography variant="h4">
                      <Box fontSize={13} className={classes.textSecondary}>
                        {forceTimezone(moment(current?.created_time)).format(
                          `${localeFormat} hh:mm A`
                        )}
                      </Box>
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <CollapseCard
                  customClasses={collapseCardClasses}
                  title={i18n.t('alarm.current_information')}
                >
                  <Typography variant="h4">
                    <Box fontSize={13} className={classes.textSecondary}>
                      {current?.current_information?.map((information, index) => (
                        <Typography variant="h4" key={index}>
                          <Box fontSize={13} className={classes.textSecondary}>
                            {`${information?.name}: ${information?.value} `}
                          </Box>
                        </Typography>
                      ))}
                    </Box>
                  </Typography>
                </CollapseCard>
              </Box>
              {related !== 'task' && (
                <Box>
                  <CollapseCard title={taskTitle} customClasses={collapseCardClasses}>
                    {current?.tasks?.length > 0 ? (
                      <Typography variant="h4">
                        <Box fontSize={13} className={classes.textSecondary}>
                          {current?.tasks?.map((task, index) => (
                            <ItemDetails
                              key={index}
                              id={task?.id}
                              title={task?.title}
                              date={task?.due_time}
                              user={task?.owner_user}
                              priority={task?.priority}
                              description={task?.description}
                              handleItemClick={handleItemClick}
                              dateLabel={i18n.t('common.due_time')}
                              userLabel={i18n.t('tasks.assigned_to')}
                            />
                          ))}
                        </Box>
                      </Typography>
                    ) : (
                      <Typography variant="h4" align="center">
                        <Box fontSize={12} className={classes.textSecondary} py={3}>
                          {i18n.t('tasks.tasks_placeholder')}
                        </Box>
                      </Typography>
                    )}
                  </CollapseCard>
                </Box>
              )}
              <Box display="flex" alignItems="center">
                <Typography variant="h4">
                  <Box
                    mr={2}
                    fontSize={13}
                    fontWeight="fontWeightBold"
                    className={classes.textSecondary}
                  >
                    {i18n.t('common.tags')}:
                  </Box>
                </Typography>
                <Box className={classes.tagsContainer}>
                  {current?.relationships?.map((tag) => (
                    <Chip
                      className={classes.tag}
                      key={tag?.id}
                      label={`${tag?.class} ID#${tag?.id}`}
                      clickable
                      onClick={() => handleTagClick(tag?.class, tag?.id)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </>
        )}
        {openCreateTaskModal && (
          <CreateEditTaskModal
            id={id}
            type="alarm"
            open={openCreateTaskModal}
            handleClick={handleOpenCreateTaskModal}
          />
        )}
      </>
    </Card>
  );
};

export default withRouter(AlarmDetails);
